import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    useTheme,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { noticeAPI, notificationAPI, membershipAPI } from '../../actions/imsServer';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import dayjs from 'dayjs';
import RadioBt, { RadioGroup } from '../../components/radioBt';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Dropdown from '../../components/dropdown';
import Button from '../../components/button';
import ContentArea from '../../components/contentArea';
import Toggle from '../../components/toggle';
import { HeaderCell } from '../../components/table/headerCell';
import { getTableStyles } from '../../components/table/styles';
import { Prev, Next } from '../../assets/images';
import { isSupervisor } from '../../utils/base';
import { NOTICE_TYPES, API_PAGE_SIZE } from '../../constants';
import { FormLabel } from '../../components/formItem';
import InputField from '../../components/formItem/inputField';
import Toast from '../../components/toast';

export const NoticeAdmin = () => {
    const theme = useTheme();
    const { t } = useTranslation();

    const [tab, setTab] = useState(0);
    const [notices, setNotices] = useState([]);
    const [pushes, setPushes] = useState([]);
    const [, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const [addTab, setAddTab] = useState(0);
    const [addTitle, setAddTitle] = useState('');
    const [addContent, setAddContent] = useState('');
    const [addFile, setAddFile] = useState(null);
    const [uploadType, setUploadType] = useState('immediate');
    const [reserveDate, setReserveDate] = useState(
        dayjs().format('YYYY-MM-DD')
    );
    const [reserveHour, setReserveHour] = useState(dayjs().format('hh'));
    const [reserveMinute, setReserveMinute] = useState(() => {
        const currentMinute = parseInt(dayjs().format('mm'));
        return Math.ceil(currentMinute / 5) * 5;
    });
    const [addLoading, setAddLoading] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [addNotificationType, setAddNotificationType] = useState('0');
    const [editTitle, setEditTitle] = useState('');
    const [editContent, setEditContent] = useState('');
    const [editFile, setEditFile] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const ITEMS_PER_PAGE = API_PAGE_SIZE;
    const [meInfo, setMeInfo] = useState(JSON.parse(localStorage.getItem('meInfo')));
    const [organizationId, setOrganizationId] = useState(0);
    const [organizationName, setOrganizationName] = useState('all');
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastSeverity, setToastSeverity] = useState('success');

    const showToast = (message, severity = 'success') => {
        setToastMessage(message);
        setToastSeverity(severity);
        setToastOpen(true);
    };

    useEffect(() => {
        setMeInfo(JSON.parse(localStorage.getItem('meInfo')));

        const fetchAdminOrganization = async () => {
            const me = JSON.parse(localStorage.getItem('meInfo'));
            const isSuper = isSupervisor(me?.grade);
            if (!isSuper && me?.id) {
                try {
                    // grade 50, 60 모두 membership에서 가져오기
                    const membershipsResponse = await membershipAPI.getMemberships({ userId: me.id, type: 0, state: 0, expand: 'organization' });
                    const memberships = membershipsResponse?.data?.memberships || [];
                    if (memberships.length > 0) {
                        setOrganizationId(memberships[0].organizationId);
                        setOrganizationName(memberships[0].organization?.name || '');
                    }
                } catch (orgErr) {
                    console.error('어드민 기관 정보 가져오기 실패:', orgErr);
                }
            }
        }
        fetchAdminOrganization();
    }, []);

    const fetchNotices = async (page = currentPage) => {
        try {
            setLoading(true);
            const offset = (page - 1) * ITEMS_PER_PAGE;
            const res = await noticeAPI.getNotices({
                ...(organizationId !== undefined && { organizationId }),
                state: '0,2',
                expand: 'organization',
                offset,
                limit: ITEMS_PER_PAGE,
            });
            const noticesData = Array.isArray(res) ? res : res.data.notices || [];
            // 서버에서 totalCount 받아오기 (API 응답 구조에 따라 조정 필요)
            const total = res.data?.total || noticesData.length;
            setNotices(noticesData);
            setTotalCount(total);
        } catch (e) {
            setNotices([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchPushes = async (page = currentPage) => {
        try {
            setLoading(true);
            const offset = (page - 1) * ITEMS_PER_PAGE;
            const res = await notificationAPI.getNotifications({
                ...(organizationId !== undefined && { organizationId }),
                type: '0,1,2',
                state: '0,2',
                expand: 'organization',
                offset,
                limit: ITEMS_PER_PAGE,
            });
            const allPushes = Array.isArray(res) ? res : res.data.notifications || [];
            // 슈퍼어드민이면 전체 데이터, 어드민이면 해당 기관 데이터만 필터링
            // 서버사이드 페이징이므로 클라이언트에서 필터링하지 않음
            // 단, 어드민인 경우 서버에서 이미 필터링된 데이터가 올 것으로 예상
            const filteredPushes = isSupervisor(meInfo?.grade)
                ? allPushes
                : allPushes.filter(
                    notification =>
                        notification.organizationId === 0 ||
                        notification.organizationId === organizationId
                );
            // 서버에서 totalCount 받아오기 (API 응답 구조에 따라 조정 필요)
            const total = res.data?.total || filteredPushes.length;
            setPushes(filteredPushes);
            setTotalCount(total);
        } catch (e) {
            setPushes([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (tab === 0) {
            fetchNotices(1);
        } else {
            fetchPushes(1);
        }
        setCurrentPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchNotices/fetchPushes는 매 렌더 새 참조라 의존성에서 제외(반복 호출 방지)
    }, [tab]);

    // 페이지 변경 시 데이터 다시 가져오기
    useEffect(() => {
        if (organizationId > 0) {
            if (tab === 0) {
                fetchNotices(currentPage);
            } else {
                fetchPushes(currentPage);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchNotices/fetchPushes는 매 렌더 새 참조라 의존성에서 제외(반복 호출 방지)
    }, [currentPage, organizationId, tab]);


    const handleRowClick = row => {
        handleModalOpen(row);
    };

    const handleModalOpen = row => {
        setSelectedRow(row);
        setModalOpen(true);

        // 모달이 열릴 때 바로 수정 데이터 설정
        setEditTitle(row.title);
        setEditContent(row.content);
        setEditFile(row.file);
    };

    const handleEditSubmit = async () => {
        if (!selectedRow) return;

        if (!editTitle.trim() || !editContent.trim()) {
            showToast(t('notice_please_enter_title_content'), 'warning');
            return;
        }

        setEditLoading(true);
        try {
            if (tab === 0) {
                // 공지사항 수정
                await noticeAPI.updateNotice({
                    id: selectedRow.id,
                    title: editTitle,
                    content: editContent,
                    file: editFile,
                });
                showToast(t('notice_updated'), 'success');
            } else {
                // 푸시알림 수정
                await notificationAPI.updateNotification({
                    id: selectedRow.id,
                    title: editTitle,
                    content: editContent,
                });
                showToast(t('notification_updated'), 'success');
            }

            setModalOpen(false);
            setSelectedRow(null);
            setEditTitle('');
            setEditContent('');
            setEditFile(null);

            // 목록 새로고침
            if (tab === 0) {
                fetchNotices(currentPage);
            } else {
                fetchPushes(currentPage);
            }
        } catch (e) {
            console.error('수정 실패:', e);
            showToast(`${t('update_failed')}: ${e.message}`, 'error');
        } finally {
            setEditLoading(false);
        }
    };

    const handleEditCancel = () => {
        setModalOpen(false);
        setSelectedRow(null);
        setEditTitle('');
        setEditContent('');
        setEditFile(null);
    };

    const stateToText = (state, startDate) => {
        if (state === 0) {
            return t('published');
        }
        if (state === 2) {
            if (startDate) {
                const now = dayjs();
                const startDateTime = dayjs(startDate);
                if (now.isBefore(startDateTime)) {
                    return t('scheduled');
                }
                return t('published');
            }
            return t('scheduled');
        }
        return '-';
    };

    // 현재 시간이 예약 시간보다 이후인지 확인하는 함수
    const isPastTime = (date, hour, minute) => {
        const now = dayjs();
        const reserveTime = dayjs(`${date} ${hour}:${minute}:00`);
        return now.isAfter(reserveTime);
    };

    // 예약 시간 변경 핸들러
    const handleReserveTimeChange = (newDate, newHour, newMinute) => {
        if (isPastTime(newDate, newHour, newMinute)) {
            showToast(t('past_time_cannot_be_selected'), 'warning');
            return;
        }
        setReserveDate(newDate);
        setReserveHour(newHour);
        setReserveMinute(newMinute);
    };

    const handleAddSubmit = async () => {
        if (addTab === 0) {
            // 공지 탭만 등록
            if (!addTitle.trim() || !addContent.trim()) {
                showToast('제목과 내용을 입력해 주세요.', 'warning');
                return;
            }
            setAddLoading(true);
            try {
                const state = uploadType === 'immediate' ? 0 : 2;
                const startDate =
                    uploadType === 'reserve'
                        ? `${reserveDate} ${reserveHour}:${reserveMinute}:00`
                        : undefined;

                await noticeAPI.registerNotice({
                    type: organizationId === 0 ? 0 : 1,
                    ...(organizationId !== undefined && { organizationId }),
                    state,
                    startDate,
                    title: addTitle,
                    content: addContent,
                    file: addFile,
                });
                showToast(t('notice_registered_successfully'), 'success');
                setAddOpen(false);
                setAddTitle('');
                setAddContent('');
                setAddFile(null);
                setUploadType('immediate');
                setReserveDate(dayjs().format('YYYY-MM-DD'));
                setReserveHour(dayjs().format('hh'));
                setReserveMinute(() => {
                    const currentMinute = parseInt(dayjs().format('mm'));
                    return Math.ceil(currentMinute / 5) * 5;
                });
                fetchNotices(currentPage); // 목록 새로고침
            } catch (e) {
                console.error('공지 등록 실패:', e);
                showToast(`${t('notice_register_failed')}: ${e.message}`, 'error');
            } finally {
                setAddLoading(false);
            }
        } else if (addTab === 1) {
            // PUSH 탭만 등록
            if (!addTitle.trim() || !addContent.trim()) {
                showToast('제목과 내용을 입력해 주세요.', 'warning');
                return;
            }
            setAddLoading(true);
            try {
                const state = uploadType === 'immediate' ? 0 : 2;
                const startDate =
                    uploadType === 'reserve'
                        ? `${reserveDate} ${reserveHour}:${reserveMinute}:00`
                        : undefined;
                await notificationAPI.registerNotification({
                    ...(organizationId !== undefined && { organizationId }),
                    userId: 0,
                    type: Number(addNotificationType),
                    state,
                    startDate,
                    title: addTitle,
                    content: addContent,
                });
                showToast(t('notification_registered'), 'success');
                setAddOpen(false);
                setAddTitle('');
                setAddContent('');
                setAddFile(null);
                setUploadType('immediate');
                setReserveDate(dayjs().format('YYYY-MM-DD'));
                setReserveHour(dayjs().format('hh'));
                setReserveMinute(() => {
                    const currentMinute = parseInt(dayjs().format('mm'));
                    return Math.ceil(currentMinute / 5) * 5;
                });
                fetchPushes(currentPage); // 목록 새로고침
            } catch (e) {
                console.error('알림 등록 실패:', e);
                showToast(`${t('notification_register_failed')}: ${e.message}`, 'error');
            } finally {
                setAddLoading(false);
            }
        }
    };

    const handleAddCancel = () => {
        setAddTitle('');
        setAddContent('');
        setAddFile(null);
        setUploadType('immediate');
        setReserveDate(dayjs().format('YYYY-MM-DD'));
        setReserveHour(dayjs().format('hh'));
        setReserveMinute(() => {
            const currentMinute = parseInt(dayjs().format('mm'));
            return Math.ceil(currentMinute / 5) * 5;
        });
        setAddNotificationType('0');
        setAddOpen(false);
    };

    // 추가 팝업 열기
    const handleAddOpen = async () => {
        // 현재 메인 화면의 탭에 맞춰 추가 팝업의 탭도 설정
        setAddTab(tab);
        setAddOpen(true);
    };

    // 표 데이터 변환
    const rows = (tab === 0 ? notices : pushes).map((item, idx, array) => {
        // UTC 시간을 현지시각으로 변환
        const formatLocalDateTime = utcDateString => {
            if (!utcDateString) return '';

            try {
                const utcDate = new Date(utcDateString);
                const year = utcDate.getFullYear();
                const month = String(utcDate.getMonth() + 1).padStart(2, '0');
                const day = String(utcDate.getDate()).padStart(2, '0');
                const hours = String(utcDate.getHours()).padStart(2, '0');
                const minutes = String(utcDate.getMinutes()).padStart(2, '0');
                const seconds = String(utcDate.getSeconds()).padStart(2, '0');

                return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            } catch (error) {
                console.error('Date parsing error:', error);
                return utcDateString.replace('T', ' ').substring(0, 19);
            }
        };

        // 순번 계산 (서버사이드 페이징: 전체 순번 = (현재 페이지 - 1) * 페이지당 항목 수 + 인덱스 + 1)
        const sequenceNumber = (currentPage - 1) * ITEMS_PER_PAGE + idx + 1;

        return {
            id: item.id,
            number: sequenceNumber, // 순번으로 변경
            date: formatLocalDateTime(item.startDate || item.createDate),
            organization: organizationName,
            title: item.title || '',
            content: item.content || '',
            file: item.file || '',
            state: stateToText(item.state, item.startDate), // startDate 파라미터 추가
        };
    });

    // 삭제 처리 함수
    const handleDelete = async () => {
        if (!selectedRow) return;
        if (!window.confirm(t('are_you_sure_you_want_to_delete'))) return;
        try {
            if (tab === 0) {
                // 공지사항 삭제
                await noticeAPI.updateNotice({ id: selectedRow.id, state: 1 });
                fetchNotices(currentPage); // 공지 목록 새로고침
            } else if (tab === 1) {
                // 푸시알림 삭제
                await notificationAPI.updateNotification({
                    id: selectedRow.id,
                    state: 1,
                });
                fetchPushes(currentPage); // 알림 목록 새로고침
            }
            showToast(t('delete_completed'), 'success');
            setModalOpen(false);
        } catch (e) {
            showToast(t('delete_failed') + ': ' + e.message, 'error');
        }
    };

    // 페이징 핸들러
    const handlePageChange = (newPage) => {
        if (newPage < 1) return;
        const maxPage = Math.ceil(totalCount / ITEMS_PER_PAGE);
        if (newPage > maxPage && maxPage > 0) return;
        setCurrentPage(newPage);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        const maxPage = Math.ceil(totalCount / ITEMS_PER_PAGE);
        if (currentPage < maxPage) {
            handlePageChange(currentPage + 1);
        }
    };

    return (
        <ContentArea>
            {/* 탭/필터 영역 (events admin과 동일 레이아웃) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', px: 2.5, py: 2.5, mb: 3, gap: 2 }}>
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ flex: 1 }} />
                    <Toggle
                        options={NOTICE_TYPES.map(type => ({ value: type.value, label: t(type.label) }))}
                        value={tab}
                        onChange={(index) => {
                            setTab(index);
                            setCurrentPage(1);
                        }}
                        sx={{ width: 'auto', minWidth: '250px' }}
                    />
                </Box>
            </Box>

            {/* 테이블 */}
            <Box sx={{ px: 2.5, mb: 3, width: '100%' }}>
                <TableContainer
                    component={Paper}
                    sx={{
                        borderRadius: '16px',
                        overflow: 'hidden',
                        boxShadow: 'none',
                        border: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Table aria-label="notices table" sx={{ '& .MuiTableCell-root': { color: theme.palette.text.primary } }}>
                        <TableHead>
                            <TableRow>
                                <HeaderCell label={t('number')} align="center" />
                                <HeaderCell label={t('date_time')} align="center" />
                                {isSupervisor(meInfo?.grade) && (
                                    <HeaderCell label={t('target_organization')} align="center" />
                                )}
                                <HeaderCell label={t('title')} align="center" />
                                <HeaderCell label={t('content')} align="center" />
                                {tab === 0 && (
                                    <HeaderCell label={t('file')} align="center" />
                                )}
                                <HeaderCell label={t('status')} align="center" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.isArray(rows) && rows.length > 0 ? (
                                rows.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        onClick={() => handleRowClick(row)}
                                        sx={{
                                            cursor: 'pointer',
                                            ...getTableStyles(theme).tableRowStyle
                                        }}
                                    >
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {row.number}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {row.date}
                                        </TableCell>
                                        {isSupervisor(meInfo?.grade) && (
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                {row.organization === 'all' ? t('all') : row.organization}
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <Typography
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: '300px',
                                                }}
                                            >
                                                {row.title}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {row.content}
                                            </Typography>
                                        </TableCell>
                                        {tab === 0 && (
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                {row.file && (
                                                    <AttachFileIcon sx={{ color: theme.palette.primary.main }} />
                                                )}
                                            </TableCell>
                                        )}
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {row.state}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={
                                            tab === 0
                                                ? 8
                                                : (tab === 1 && isSupervisor(meInfo?.grade))
                                                    ? 6
                                                    : 5
                                        }
                                        sx={{ textAlign: 'center', py: 4 }}
                                    >
                                        {tab === 0 ? t('no_notices_registered') : t('no_push_notifications_registered')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* 페이징 컨트롤 */}
                {totalCount > 0 && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '20px 0',
                    }}>
                        <Box
                            onClick={handlePrevPage}
                            sx={{
                                width: '20px',
                                height: '20px',
                                cursor: currentPage > 1 ? 'pointer' : 'default',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: currentPage > 1 ? 1 : 0.3,
                            }}
                        >
                            <Prev color={theme.palette.figma.white[80]} size={20} />
                        </Box>
                        <Typography sx={{
                            fontSize: '16px',
                            fontWeight: 500,
                            color: theme.palette.figma.white[80],
                            letterSpacing: '-0.48px',
                            whiteSpace: 'nowrap'
                        }}>
                            {currentPage} / {Math.ceil(totalCount / ITEMS_PER_PAGE)}
                        </Typography>
                        <Box
                            onClick={handleNextPage}
                            sx={{
                                width: '20px',
                                height: '20px',
                                cursor: currentPage < Math.ceil(totalCount / ITEMS_PER_PAGE) ? 'pointer' : 'default',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: currentPage < Math.ceil(totalCount / ITEMS_PER_PAGE) ? 1 : 0.3,
                            }}
                        >
                            <Next color={theme.palette.figma.white[80]} size={20} />
                        </Box>
                    </Box>
                )}
            </Box>

            {/* 추가하기 모달 */}
            <Dialog
                open={addOpen}
                onClose={handleAddCancel}
                maxWidth="lg"
                fullWidth
                slotProps={{
                    root: { keepMounted: false },
                }}
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        background: theme.palette.figma?.popupBG,
                        maxWidth: '1200px',
                        width: '95%',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <DialogContent
                    sx={{
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                        overflow: 'auto',
                    }}
                >
                    {/* 탭 */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Toggle
                            options={NOTICE_TYPES.map(type => ({ value: type.value, label: t(type.label) }))}
                            value={addTab}
                            onChange={(index) => setAddTab(index)}
                            sx={{ width: 'auto', minWidth: '250px' }}
                        />
                    </Box>
                    {/* 공지, PUSH 폼 분기 */}
                    {addTab === 0 && (
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                minHeight: '400px',
                            }}
                        >
                            {/* 대상기관 Field */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '150px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 1.5,
                                        borderRadius: '8px',
                                    }}
                                >
                                    <FormLabel text={t('target_organization')} />
                                </Box>
                                <Box sx={{ flex: 1, ml: 2 }}>
                                    <InputField
                                        value={organizationName === 'all' ? t('all') : organizationName}
                                        disabled
                                        placeholder=""
                                        showClearIcon={false}
                                    />
                                </Box>
                            </Box>

                            {/* Title Field */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '150px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 1.5,
                                        borderRadius: '8px',
                                    }}
                                >
                                    <FormLabel text={t('title')} />
                                </Box>
                                <Box sx={{ flex: 1, ml: 2 }}>
                                    <InputField
                                        value={addTitle}
                                        onChange={e =>
                                            setAddTitle(e.target.value)
                                        }
                                        placeholder={t('please_enter')}
                                        showClearIcon={false}
                                    />
                                </Box>
                            </Box>

                            {/* Content Field */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '150px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 1.5,
                                        borderRadius: '8px',
                                        pt: 2,
                                    }}
                                >
                                    <FormLabel text={t('content')} />
                                </Box>
                                <Box sx={{ flex: 1, ml: 2 }}>
                                    <TextField
                                        value={addContent}
                                        onChange={e =>
                                            setAddContent(e.target.value)
                                        }
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        maxRows={8}
                                    />
                                </Box>
                            </Box>

                            {/* File Upload Field (공지사항만) */}
                            {addTab === 0 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: '150px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            p: 1.5,
                                            borderRadius: '8px',
                                        }}
                                    >
                                        <FormLabel text={t('attachment')} />
                                    </Box>
                                    <Box
                                        sx={{
                                            flex: 1,
                                            ml: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                        }}
                                    >
                                        <Button
                                            variant="normal"
                                            component="label"
                                            onClick={() => {
                                                const input = document.createElement('input');
                                                input.type = 'file';
                                                input.accept = 'image/*';
                                                input.onchange = (e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        if (!file.type.startsWith('image/')) {
                                                            showToast(t('unsupported_image_format_only_jpeg_png_gif_allowed'), 'error');
                                                            return;
                                                        }
                                                        setAddFile(file);
                                                    }
                                                };
                                                input.click();
                                            }}
                                            sx={{ minWidth: '120px' }}
                                        >
                                            {t('attach_photo')}
                                        </Button>
                                        <Typography>
                                            {addFile ? addFile.name : t('no_data_available')}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* Upload Condition Field */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '150px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 1.5,
                                        borderRadius: '8px',
                                    }}
                                >
                                    <FormLabel text={t('upload_condition')} />
                                </Box>
                                <Box
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <RadioGroup
                                        value={uploadType}
                                        onChange={(e) => setUploadType(e.target.value)}
                                        name="upload-type"
                                        sx={{ display: 'flex', gap: 0 }}
                                    >
                                        <RadioBt
                                            value="immediate"
                                            label={t('immediate_upload')}
                                        />
                                        <RadioBt
                                            value="reserve"
                                            label={t('scheduled_upload')}
                                        />
                                    </RadioGroup>
                                    {uploadType === 'reserve' && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <InputField
                                                type="date"
                                                value={reserveDate}
                                                onChange={(e) => {
                                                    if (e.target.value) {
                                                        handleReserveTimeChange(
                                                            e.target.value,
                                                            reserveHour,
                                                            reserveMinute
                                                        );
                                                    }
                                                }}
                                                showClearIcon={false}
                                            />
                                            <Dropdown
                                                value={reserveHour}
                                                onChange={(option) =>
                                                    handleReserveTimeChange(
                                                        reserveDate,
                                                        option.value,
                                                        reserveMinute
                                                    )
                                                }
                                                options={[
                                                    ...Array(24).keys(),
                                                ].map(h => {
                                                    const hour = h
                                                        .toString()
                                                        .padStart(2, '0');
                                                    const isDisabled =
                                                        isPastTime(
                                                            reserveDate,
                                                            hour,
                                                            reserveMinute
                                                        );
                                                    return {
                                                        value: hour,
                                                        label: `${h}${t('hour')}`,
                                                        disabled:
                                                            isDisabled,
                                                    };
                                                }).filter(opt => !opt.disabled)}
                                                placeholder={t('hour')}
                                                sx={{
                                                    ml: 1,
                                                    mr: 1,
                                                    width: '170px',
                                                }}
                                            />
                                            <Dropdown
                                                value={reserveMinute}
                                                onChange={(option) =>
                                                    handleReserveTimeChange(
                                                        reserveDate,
                                                        reserveHour,
                                                        option.value
                                                    )
                                                }
                                                options={[
                                                    ...Array(12).keys(),
                                                ].map(m => {
                                                    const minute = (m * 5)
                                                        .toString()
                                                        .padStart(2, '0');
                                                    const isDisabled =
                                                        isPastTime(
                                                            reserveDate,
                                                            reserveHour,
                                                            minute
                                                        );
                                                    return {
                                                        value: minute,
                                                        label: `${minute}${t('min')}`,
                                                        disabled:
                                                            isDisabled,
                                                    };
                                                }).filter(opt => !opt.disabled)}
                                                placeholder={t('min')}
                                                sx={{
                                                    mr: 1,
                                                    width: '170px',
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    )}
                    {addTab === 1 && (
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                minHeight: '400px',
                            }}
                        >
                            {/* 대상기관 Field */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '150px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 1.5,
                                        borderRadius: '8px',
                                    }}
                                >
                                    <FormLabel text={t('target_organization')} />
                                </Box>
                                <Box sx={{ flex: 1, ml: 2 }}>
                                    <InputField
                                        value={organizationName === 'all' ? t('all') : organizationName}
                                        disabled
                                        placeholder=""
                                        showClearIcon={false}
                                    />
                                </Box>
                            </Box>

                            {/* Title Field */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '150px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 1.5,
                                        borderRadius: '8px',
                                    }}
                                >
                                    <FormLabel text={t('title')} />
                                </Box>
                                <Box sx={{ flex: 1, ml: 2 }}>
                                    <InputField
                                        value={addTitle}
                                        onChange={e =>
                                            setAddTitle(e.target.value)
                                        }
                                        placeholder={t('please_enter')}
                                        showClearIcon={false}
                                    />
                                </Box>
                            </Box>

                            {/* Content Field */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '150px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 1.5,
                                        borderRadius: '8px',
                                        pt: 2,
                                    }}
                                >
                                    <FormLabel text={t('content')} />
                                </Box>
                                <Box sx={{ flex: 1, ml: 2 }}>
                                    <TextField
                                        value={addContent}
                                        onChange={e =>
                                            setAddContent(e.target.value)
                                        }
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        maxRows={8}
                                    />
                                </Box>
                            </Box>

                            {/* Upload Condition Field */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '150px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 1.5,
                                        borderRadius: '8px',
                                    }}
                                >
                                    <FormLabel text={t('upload_condition')} />
                                </Box>
                                <Box
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <RadioGroup
                                        value={uploadType}
                                        onChange={(e) => setUploadType(e.target.value)}
                                        name="upload-type"
                                        sx={{ display: 'flex', gap: 0 }}
                                    >
                                        <RadioBt
                                            value="immediate"
                                            label={t('immediate_upload')}
                                        />
                                        <RadioBt
                                            value="reserve"
                                            label={t('scheduled_upload')}
                                        />
                                    </RadioGroup>
                                    {uploadType === 'reserve' && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <InputField
                                                type="date"
                                                value={reserveDate}
                                                onChange={(e) => {
                                                    if (e.target.value) {
                                                        handleReserveTimeChange(
                                                            e.target.value,
                                                            reserveHour,
                                                            reserveMinute
                                                        );
                                                    }
                                                }}
                                                showClearIcon={false}
                                            />
                                            <Dropdown
                                                value={reserveHour}
                                                onChange={(option) =>
                                                    handleReserveTimeChange(
                                                        reserveDate,
                                                        option.value,
                                                        reserveMinute
                                                    )
                                                }
                                                options={[
                                                    ...Array(24).keys(),
                                                ].map(h => {
                                                    const hour = h
                                                        .toString()
                                                        .padStart(2, '0');
                                                    const isDisabled =
                                                        isPastTime(
                                                            reserveDate,
                                                            hour,
                                                            reserveMinute
                                                        );
                                                    return {
                                                        value: hour,
                                                        label: `${h}${t('hour')}`,
                                                        disabled:
                                                            isDisabled,
                                                    };
                                                }).filter(opt => !opt.disabled)}
                                                placeholder={t('hour')}
                                                sx={{
                                                    ml: 1,
                                                    mr: 1,
                                                    width: '170px',
                                                }}
                                            />
                                            <Dropdown
                                                value={reserveMinute}
                                                onChange={(option) =>
                                                    handleReserveTimeChange(
                                                        reserveDate,
                                                        reserveHour,
                                                        option.value
                                                    )
                                                }
                                                options={[
                                                    ...Array(12).keys(),
                                                ].map(m => {
                                                    const minute = (m * 5)
                                                        .toString()
                                                        .padStart(2, '0');
                                                    const isDisabled =
                                                        isPastTime(
                                                            reserveDate,
                                                            reserveHour,
                                                            minute
                                                        );
                                                    return {
                                                        value: minute,
                                                        label: `${minute}${t('min')}`,
                                                        disabled:
                                                            isDisabled,
                                                    };
                                                }).filter(opt => !opt.disabled)}
                                                placeholder={t('min')}
                                                sx={{
                                                    mr: 1,
                                                    width: '170px',
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{
                    padding: '20px',
                    justifyContent: 'center',
                    gap: '10px',
                }}>
                    <Button
                        variant="gray"
                        onClick={handleAddCancel}
                        disabled={addLoading}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleAddSubmit}
                        disabled={addLoading}
                    >
                        {addLoading ? t('saving') : t('save')}
                    </Button>
                    {addTab === 0 && (
                        <Button
                            onClick={() => setPreviewOpen(true)}
                            disabled={addLoading}
                        >
                            {t('mobile_preview')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            {/* {t('mobile_preview')} 모달 */}
            {addTab === 0 && (
                <Dialog
                    open={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                >
                    <Box
                        sx={{
                            borderRadius: 0,
                            p: 0,
                            minWidth: 360,
                        }}
                    >
                        <DialogTitle sx={{ p: 0, m: 0, display: 'none' }}>
                            {t('mobile_preview')}
                        </DialogTitle>
                        <DialogContent
                            sx={{
                                bgcolor: theme.palette.figma.popupBG,
                                p: 0,
                                minHeight: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    width: 360,
                                    height: 780,
                                    maxWidth: '100%',
                                    maxHeight: '100vh',
                                    borderRadius: 2,
                                    boxShadow: 2,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                {/* Header */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        height: '54px',
                                        paddingLeft: '20px',
                                        paddingRight: '20px',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'flex-start',
                                        gap: '10px',
                                        alignSelf: 'stretch',
                                        borderBottom:
                                            `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            alignSelf: 'stretch',
                                        }}
                                    >
                                        <Box sx={{ width: 20, height: 20 }} />
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontSize: '15px',
                                                fontWeight: 600,
                                                lineHeight: '20px',
                                            }}
                                        >
                                            {t('notices')}
                                        </Typography>
                                    </Box>
                                </Box>
                                {/* Notice Detail */}
                                <Box
                                    sx={{
                                        padding: '0px 20px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        flex: 1,
                                        alignSelf: 'stretch',
                                        minHeight: 0,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            minWidth: '280px',
                                            padding: '20px 0px 10px 0px',
                                            alignItems: 'center',
                                            gap: '5px',
                                            alignSelf: 'stretch',
                                            borderBottom:
                                                `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'flex-start',
                                                gap: '10px',
                                                flex: 1,
                                                alignSelf: 'stretch',
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontSize: '15px',
                                                    fontWeight: 500,
                                                    lineHeight: 'normal',
                                                    textAlign: 'left',
                                                    wordBreak: 'break-all',
                                                }}
                                            >
                                                {addTitle || t('title_preview')}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    overflow: 'hidden',
                                                    color: theme.palette.text.disabled,
                                                    textOverflow: 'ellipsis',
                                                    fontSize: '12px',
                                                    fontStyle: 'normal',
                                                    fontWeight: 500,
                                                    lineHeight: 'normal',
                                                }}
                                            >
                                                {/* 예약 업로드면 예약일시, 아니면 현재 일시 */}
                                                {uploadType === 'reserve'
                                                    ? `${reserveDate} ${reserveHour}:${reserveMinute}`
                                                    : new Date().toLocaleString(
                                                        'ko-KR',
                                                        {
                                                            year: 'numeric',
                                                            month: '2-digit',
                                                            day: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                        }
                                                    )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            minWidth: '280px',
                                            padding: '20px 0px',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'flex-start',
                                            gap: '5px',
                                            alignSelf: 'stretch',
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                alignSelf: 'stretch',
                                                fontSize: '15px',
                                                fontStyle: 'normal',
                                                fontWeight: 500,
                                                lineHeight: 'normal',
                                                textAlign: 'left',
                                                wordBreak: 'break-all',
                                                whiteSpace: 'pre-line',
                                            }}
                                        >
                                            {addContent || t('content_preview')}
                                        </Typography>
                                        {addFile && (
                                            <Box
                                                sx={{
                                                    mt: 2,
                                                    width: '100%',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={URL.createObjectURL(
                                                        addFile
                                                    )}
                                                    alt={t('attachment_file')}
                                                    sx={{
                                                        maxWidth: '100%',
                                                        maxHeight: '200px',
                                                        objectFit: 'contain',
                                                        borderRadius: '8px',
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </DialogContent>
                    </Box>
                </Dialog>
            )}

            {/* 하단 추가하기 버튼 */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2.5, pb: 3, alignSelf: 'stretch' }}>
                <Button onClick={handleAddOpen}>
                    {t('add')}
                </Button>
            </Box>

            {/* 편집 모달 (공지사항, 푸시알림) */}
            <Dialog
                open={modalOpen}
                onClose={handleEditCancel}
                maxWidth="lg"
                fullWidth
                slotProps={{
                    root: { keepMounted: false },
                }}
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        background: theme.palette.figma?.popupBG,
                        maxWidth: '1200px',
                        width: '95%',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <DialogContent
                    sx={{
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                        overflow: 'auto',
                    }}
                >
                    {selectedRow && (
                        <>
                            {/* 번호 */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '150px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 1.5,
                                        borderRadius: '8px',
                                    }}
                                >
                                    <FormLabel text={t('number')} />
                                </Box>
                                <Box sx={{ flex: 1, ml: 2 }}>
                                    <InputField
                                        value={selectedRow.number}
                                        disabled
                                        placeholder=""
                                        showClearIcon={false}
                                    />
                                </Box>
                            </Box>

                            {/* 일시 */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '150px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 1.5,
                                        borderRadius: '8px',
                                    }}
                                >
                                    <FormLabel text={t('date_time')} />
                                </Box>
                                <Box sx={{ flex: 1, ml: 2 }}>
                                    <InputField
                                        value={selectedRow.date}
                                        disabled
                                        placeholder=""
                                        showClearIcon={false}
                                    />
                                </Box>
                            </Box>

                            {/* 대상기관 */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '150px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 1.5,
                                        borderRadius: '8px',
                                    }}
                                >
                                    <FormLabel text={t('target_organization')} />
                                </Box>
                                <Box sx={{ flex: 1, ml: 2 }}>
                                    <InputField
                                        value={selectedRow.organization || t('all')}
                                        disabled
                                        placeholder=""
                                        showClearIcon={false}
                                    />
                                </Box>
                            </Box>

                            {/* 제목 */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '150px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 1.5,
                                        borderRadius: '8px',
                                    }}
                                >
                                    <FormLabel text={t('title')} />
                                </Box>
                                <Box sx={{ flex: 1, ml: 2 }}>
                                    <InputField
                                        value={editTitle}
                                        onChange={(e) => setEditTitle(e.target.value)}
                                        placeholder=""
                                        showClearIcon={false}
                                    />
                                </Box>
                            </Box>

                            {/* 내용 */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '150px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 1.5,
                                        borderRadius: '8px',
                                        pt: 2,
                                    }}
                                >
                                    <FormLabel text={t('content')} />
                                </Box>
                                <Box sx={{ flex: 1, ml: 2 }}>
                                    <TextField
                                        value={editContent}
                                        onChange={(e) => setEditContent(e.target.value)}
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        maxRows={8}
                                    />
                                </Box>
                            </Box>

                            {/* 첨부 (공지사항만) */}
                            {tab === 0 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: '150px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            p: 1.5,
                                            borderRadius: '8px',
                                        }}
                                    >
                                        <FormLabel text={t('attachment')} />
                                    </Box>
                                    <Box sx={{ flex: 1, ml: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Button
                                            variant="normal"
                                            onClick={() => {
                                                const input = document.createElement('input');
                                                input.type = 'file';
                                                input.accept = 'image/*';
                                                input.onchange = (e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        setEditFile(file);
                                                    }
                                                };
                                                input.click();
                                            }}
                                        >
                                            {t('attach_photo')}
                                        </Button>
                                        <Typography sx={{ color: theme.palette.text.secondary }}>
                                            {editFile ? editFile.name : t('no_data_available')}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{
                    padding: '20px',
                    justifyContent: 'center',
                    gap: 2,
                }}>
                    <Button
                        variant="gray"
                        onClick={handleEditCancel}
                        disabled={editLoading}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleEditSubmit}
                        disabled={editLoading}
                    >
                        {editLoading ? t('saving') : t('save')}
                    </Button>
                    <Button
                        variant="gray"
                        onClick={handleDelete}
                        disabled={editLoading}
                    >
                        {t('delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Toast
                open={toastOpen}
                message={toastMessage}
                severity={toastSeverity}
                onClose={() => setToastOpen(false)}
            />
        </ContentArea>
    );
};
